import { Box, SxProps } from '@mui/system'
import React, { useEffect, useState } from 'react'

import { Step1 } from './Step1'
import { Step2 } from './Step2'
import { Step4 } from './Step4'
import { useNavigate, useParams } from 'react-router-dom'
import { Step3 } from './Step3'
import { ProductInfo, useProduct } from '../../context/product_context'
import { useSnack } from '../../context/snack_context'
import { TitleHeader } from '../TitleHeader'
import { Stepper } from '../Stepper'
import { StyledPaper } from '../StyledPaper'
import { ActionsFooter } from '../ActionsFooter'
import { useAccount } from '../../context/account_context'
import GenericModal from '../GenericModal'
import { useTranslation } from 'react-i18next'

interface Props {
    isEditing: boolean
    isMassEditing: boolean
    handleClose(): void
    returnNewProduct(p: ProductInfo): void
    editIdFromProps?: number
    showTitleHeader?: boolean
    elevation?: number
    sx?: SxProps
}

export const RegisterProduct = ({
    isEditing,
    isMassEditing,
    handleClose,
    editIdFromProps,
    showTitleHeader,
    elevation,
    sx,
    returnNewProduct,
}: Props) => {
    const {
        productInfo,
        setProductInfo,
        validate,
        handleCreateNewProduct,
        startNewProduct,
        startMassEdit,
        editProduct,
        handleMassEdit,
        filters,
        massEditSelectedFields,
    } = useProduct()
    const { t } = useTranslation()
    const navigate = useNavigate()
    const snack = useSnack()
    const { id } = useParams()
    const { accountInfo } = useAccount()
    const [openMassEditProductsModal, setOpenMassEditProductsModal] =
        useState(false)

    const canUpdateProduct =
        accountInfo?.isMaster ||
        accountInfo?.rules.findIndex(
            (r) =>
                r.group.toLowerCase() === 'produtos' &&
                r.name.toLowerCase() === 'atualizar'
        ) !== -1

    const canUpdateProductBarcode =
        accountInfo?.isMaster ||
        accountInfo?.rules.findIndex(
            (r) =>
                r.group.toLowerCase() === 'produtos' &&
                r.name.toLowerCase() === 'editar código de barras'
        ) !== -1

    const canUpdateProductInternalcode =
        accountInfo?.isMaster ||
        !!accountInfo?.permissions?.includes('Produtos:Editar código interno')

    const canUpdateProductImage =
        accountInfo?.isMaster ||
        !!accountInfo?.permissions?.includes('Produtos:Editar imagem')

    const canUpdateProductLocalization =
        accountInfo?.isMaster ||
        accountInfo?.rules.findIndex(
            (r) =>
                r.group.toLowerCase() === 'produtos' &&
                r.name.toLowerCase() === 'editar localização'
        ) !== -1

    useEffect(() => {
        if (!accountInfo) return

        if (
            isEditing &&
            !canUpdateProduct &&
            !canUpdateProductBarcode &&
            !canUpdateProductInternalcode &&
            !canUpdateProductImage &&
            !canUpdateProductLocalization
        ) {
            navigate('/system/product')
            return
        }

        handleInit()
    }, [accountInfo])

    const handleInit = async () => {
        try {
            const selectedId = editIdFromProps ? editIdFromProps : id

            if (isEditing) {
                if (selectedId && !isNaN(Number(selectedId)))
                    await editProduct(Number(selectedId))
                else {
                    snack.connectionFail(new Error('Id inválido'))
                    handleClose()
                }
            } else if (isMassEditing) startMassEdit()
            else startNewProduct()
        } catch (error) {
            handleClose()
        }
    }

    return (
        <>
            <GenericModal
                open={openMassEditProductsModal}
                title="Editar productos"
                textButton="Confirmar"
                handleSubmit={() => {
                    handleMassEdit()
                    setOpenMassEditProductsModal(false)
                }}
                handleClose={setOpenMassEditProductsModal}
                handleClickSecondaryButton={() =>
                    setOpenMassEditProductsModal(false)
                }
            >
                <Box width={'350px'} textAlign={'left'}>
                    {t(
                        `¿Estás seguro de que deseas editar ${filters.rowsCount} productos? Esta acción no se puede revertir.`
                    )}
                </Box>
            </GenericModal>
            <Box>
                {productInfo.step === 1 && (
                    <Step1
                        isEditing={isEditing}
                        isMassEditing={isMassEditing}
                    />
                )}
                {productInfo.step === 2 && <Step2 />}
                {productInfo.step === 3 && <Step3 />}
                {productInfo.step === 4 && <Step4 />}

                <ActionsFooter
                    cancelLabel="Volver"
                    okLabel="Continuar"
                    cancelClick={handleClose}
                    okClick={async () => {
                        if(isMassEditing) {
                            if(!filters.rowsCount) {
                                snack.error('Ningun producto con estes filtros encontrado.')
                                return;
                            }

                            if(!massEditSelectedFields.filter(m => m.selected).length) {
                                snack.error('Seleccione al menos 1 campo para editar.')
                                return;
                            }
                            
                            setOpenMassEditProductsModal(true)
                            return;
                        }

                        if (validate()) {
                            const p = await handleCreateNewProduct()
                            if (p) returnNewProduct(p)
                        } else {
                            window.scroll(0, 0)
                        }
                    }}
                />
            </Box>
        </>
    )
}
